import React, { useState } from 'react';
import {
  Bell,
  Mail,
  Server,
  Settings,
  Shield,
  Send,
  Clock,
  Calendar,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle,
  Loader2,
  Save,
  RefreshCw,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  TestTube,
  Webhook,
  FileText,
  Users,
  Globe,
  Lock,
  Zap,
  Volume2,
  VolumeX,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
} from 'lucide-react';
import { useAuth, ROLES } from '../context/AuthContext';
import { useNotificationConfig } from '../context/NotificationConfigContext';
import { useCompanyTheme } from '../context/CompanyThemeContext';
import { useTranslation } from '../context/LanguageContext';

const NotificationSettings = () => {
  const { t } = useTranslation();
  const { user, hasRole } = useAuth();
  const { config, updateSmtpConfig, updateRules, updateEmailBranding, addWebhook, removeWebhook, testSmtpConnection, sendTestEmail, testStatus, resetConfig } = useNotificationConfig();
  const { theme } = useCompanyTheme();

  const [activeTab, setActiveTab] = useState('smtp');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [testEmail, setTestEmail] = useState(user?.email || '');


  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    events: ['breach'],
    secret: '',
  });
  const [showWebhookForm, setShowWebhookForm] = useState(false);

  const tabs = [
    { id: 'smtp', label: t('notificationSettings.tabs.smtp'), icon: Mail },
    { id: 'alerts', label: t('notificationSettings.tabs.alerts'), icon: Bell },
    { id: 'templates', label: t('notificationSettings.tabs.templates'), icon: FileText },
    { id: 'webhooks', label: t('notificationSettings.tabs.webhooks'), icon: Webhook },
    { id: 'advanced', label: t('notificationSettings.tabs.advanced'), icon: Settings },
  ];

  const handleSave = async () => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsLoading(false);
    setMessage({ type: 'success', text: t('notificationSettings.messages.saved') });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleTestSmtp = async () => {
    await testSmtpConnection();
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      setMessage({ type: 'error', text: t('notificationSettings.messages.enterEmail') });
      return;
    }
    await sendTestEmail(testEmail);
  };

  const handleAddWebhook = () => {
    if (!newWebhook.name || !newWebhook.url) {
      setMessage({ type: 'error', text: t('notificationSettings.messages.webhookRequired') });
      return;
    }
    addWebhook(newWebhook);
    setNewWebhook({ name: '', url: '', events: ['breach'], secret: '' });
    setShowWebhookForm(false);
    setMessage({ type: 'success', text: t('notificationSettings.messages.webhookAdded') });
  };

  const generateSecret = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let secret = '';
    for (let i = 0; i < 32; i++) {
      secret += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewWebhook(prev => ({ ...prev, secret }));
  };


  const Toggle = ({ enabled, onChange, label, description }) => (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1">
        <p className="font-medium text-slate-800">{label}</p>
        {description && <p className="text-sm text-slate-500">{description}</p>}
      </div>
      <button
        onClick={() => onChange(!enabled)}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          enabled ? 'bg-blue-600' : 'bg-slate-300'
        }`}
      >
        <div
          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
            enabled ? 'translate-x-7' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );


  if (!hasRole(ROLES.AGENT)) {
    return (
      <div className="p-6">
        <div className="max-w-2xl mx-auto text-center py-16">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">{t('notificationSettings.restricted.title')}</h2>
          <p className="text-slate-500">
            {t('notificationSettings.restricted.message')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        {}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <Bell className="w-7 h-7 text-blue-600" />
            {t('notificationSettings.title')}
          </h1>
          <p className="text-slate-500 mt-1">
            {t('notificationSettings.subtitle')}
          </p>
        </div>

        {}
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <p>{message.text}</p>
          </div>
        )}

        {}
        {testStatus && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
              testStatus.status === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : testStatus.status === 'error'
                ? 'bg-red-50 border border-red-200 text-red-700'
                : 'bg-blue-50 border border-blue-200 text-blue-700'
            }`}
          >
            {testStatus.status === 'testing' || testStatus.status === 'sending' ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : testStatus.status === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <p>{testStatus.message}</p>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-6">
          {}
          <div className="lg:w-64 flex-shrink-0">
            <nav className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                      : 'text-slate-600 hover:bg-slate-50 border-l-4 border-transparent'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium text-sm">{tab.label}</span>
                </button>
              ))}
            </nav>

            {}
            <div className="mt-6 bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">{t('notificationSettings.status.title')}</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${config.smtp.enabled ? 'bg-green-500' : 'bg-slate-300'}`} />
                  <span className="text-slate-600">SMTP {config.smtp.enabled ? t('notificationSettings.status.enabled') : t('notificationSettings.status.disabled')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${config.rules.alertsEnabled ? 'bg-green-500' : 'bg-slate-300'}`} />
                  <span className="text-slate-600">{t('notificationSettings.alerts.title')} {config.rules.alertsEnabled ? t('notificationSettings.status.enabledFem') : t('notificationSettings.status.disabledFem')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${config.webhooks.enabled ? 'bg-green-500' : 'bg-slate-300'}`} />
                  <span className="text-slate-600">Webhooks ({config.webhooks.endpoints.length})</span>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="flex-1">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

              {}
              {activeTab === 'smtp' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-800">{t('notificationSettings.smtp.title')}</h2>
                      <p className="text-sm text-slate-500">{t('notificationSettings.smtp.subtitle')}</p>
                    </div>
                    <Toggle
                      enabled={config.smtp.enabled}
                      onChange={(enabled) => updateSmtpConfig({ enabled })}
                      label=""
                    />
                  </div>

                  {config.smtp.enabled && (
                    <div className="space-y-6">
                      {}
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                          <Server className="w-4 h-4" />
                          {t('notificationSettings.smtp.server')}
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              {t('notificationSettings.smtp.host')} *
                            </label>
                            <input
                              type="text"
                              value={config.smtp.host}
                              onChange={(e) => updateSmtpConfig({ host: e.target.value })}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="smtp.example.com"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              {t('notificationSettings.smtp.port')} *
                            </label>
                            <select
                              value={config.smtp.port}
                              onChange={(e) => updateSmtpConfig({ port: parseInt(e.target.value), secure: e.target.value === '465' })}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value={25}>25 ({t('notificationSettings.smtp.portUnsecured')})</option>
                              <option value={465}>465 (SSL)</option>
                              <option value={587}>587 (TLS - {t('notificationSettings.smtp.recommended')})</option>
                              <option value={2525}>2525 ({t('notificationSettings.smtp.alternate')})</option>
                            </select>
                          </div>
                        </div>

                        <div className="mt-4">
                          <Toggle
                            enabled={config.smtp.secure}
                            onChange={(secure) => updateSmtpConfig({ secure })}
                            label={t('notificationSettings.smtp.secureConnection')}
                            description={t('notificationSettings.smtp.secureConnectionDesc')}
                          />
                        </div>
                      </div>

                      {}
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                          <Lock className="w-4 h-4" />
                          {t('notificationSettings.smtp.authentication')}
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              {t('notificationSettings.smtp.username')} *
                            </label>
                            <input
                              type="text"
                              value={config.smtp.username}
                              onChange={(e) => updateSmtpConfig({ username: e.target.value })}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="user@example.com"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              {t('notificationSettings.smtp.password')} *
                            </label>
                            <div className="relative">
                              <input
                                type={showPassword ? 'text' : 'password'}
                                value={config.smtp.password}
                                onChange={(e) => updateSmtpConfig({ password: e.target.value })}
                                className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="••••••••"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                              >
                                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {}
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                          <Send className="w-4 h-4" />
                          {t('notificationSettings.smtp.sender')}
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              {t('notificationSettings.smtp.senderEmail')} *
                            </label>
                            <input
                              type="email"
                              value={config.smtp.fromEmail}
                              onChange={(e) => updateSmtpConfig({ fromEmail: e.target.value })}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="noreply@example.com"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              {t('notificationSettings.smtp.displayName')}
                            </label>
                            <input
                              type="text"
                              value={config.smtp.fromName}
                              onChange={(e) => updateSmtpConfig({ fromName: e.target.value })}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Covenant Tracker"
                            />
                          </div>
                        </div>
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            {t('notificationSettings.smtp.replyTo')}
                          </label>
                          <input
                            type="email"
                            value={config.smtp.replyTo}
                            onChange={(e) => updateSmtpConfig({ replyTo: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder={t('notificationSettings.smtp.replyToPlaceholder')}
                          />
                        </div>
                      </div>

                      {}
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <h3 className="text-sm font-semibold text-blue-800 mb-4 flex items-center gap-2">
                          <TestTube className="w-4 h-4" />
                          {t('notificationSettings.smtp.testConfig')}
                        </h3>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <button
                            onClick={handleTestSmtp}
                            disabled={testStatus?.status === 'testing'}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors flex items-center gap-2"
                          >
                            {testStatus?.status === 'testing' ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Zap className="w-4 h-4" />
                            )}
                            {t('notificationSettings.smtp.testConnection')}
                          </button>
                          <div className="flex-1 flex gap-2">
                            <input
                              type="email"
                              value={testEmail}
                              onChange={(e) => setTestEmail(e.target.value)}
                              className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                              placeholder={t('notificationSettings.smtp.testEmailPlaceholder')}
                            />
                            <button
                              onClick={handleSendTestEmail}
                              disabled={testStatus?.status === 'sending'}
                              className="px-4 py-2 bg-white border border-blue-300 hover:bg-blue-50 text-blue-700 font-medium rounded-lg transition-colors flex items-center gap-2"
                            >
                              <Send className="w-4 h-4" />
                              {t('common.send')}
                            </button>
                          </div>
                        </div>
                      </div>

                      {}
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <h3 className="text-sm font-semibold text-slate-800 mb-3">{t('notificationSettings.smtp.popularPresets')}</h3>
                        <div className="flex flex-wrap gap-2">
                          {[
                            { name: 'Gmail', host: 'smtp.gmail.com', port: 587 },
                            { name: 'Outlook', host: 'smtp.office365.com', port: 587 },
                            { name: 'SendGrid', host: 'smtp.sendgrid.net', port: 587 },
                            { name: 'Mailgun', host: 'smtp.mailgun.org', port: 587 },
                            { name: 'Amazon SES', host: 'email-smtp.eu-west-1.amazonaws.com', port: 587 },
                            { name: 'OVH', host: 'ssl0.ovh.net', port: 465 },
                          ].map((preset) => (
                            <button
                              key={preset.name}
                              onClick={() => updateSmtpConfig({ host: preset.host, port: preset.port, secure: preset.port === 465 })}
                              className="px-3 py-1.5 bg-white border border-slate-300 hover:border-blue-500 hover:bg-blue-50 text-slate-700 text-sm rounded-lg transition-colors"
                            >
                              {preset.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {}
              {activeTab === 'alerts' && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 mb-6">{t('notificationSettings.alerts.rulesTitle')}</h2>

                  <div className="space-y-6">
                    {}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <Toggle
                        enabled={config.rules.alertsEnabled}
                        onChange={(enabled) => updateRules({ alertsEnabled: enabled })}
                        label={t('notificationSettings.alerts.enableAlerts')}
                        description={t('notificationSettings.alerts.enableAlertsDesc')}
                      />
                    </div>

                    {config.rules.alertsEnabled && (
                      <>
                        {}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <h3 className="text-sm font-semibold text-slate-800 mb-4">{t('notificationSettings.alerts.alertTypes')}</h3>
                          <div className="space-y-3">
                            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                              <div className="flex items-center gap-3">
                                <AlertTriangle className="w-5 h-5 text-red-600" />
                                <div>
                                  <p className="font-medium text-red-800">{t('notificationSettings.alerts.breach')}</p>
                                  <p className="text-sm text-red-600">{t('notificationSettings.alerts.breachDesc')}</p>
                                </div>
                              </div>
                              <Toggle
                                enabled={config.rules.alertBreachEnabled}
                                onChange={(enabled) => updateRules({ alertBreachEnabled: enabled })}
                                label=""
                              />
                            </div>

                            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                              <div className="flex items-center gap-3">
                                <AlertCircle className="w-5 h-5 text-amber-600" />
                                <div>
                                  <p className="font-medium text-amber-800">{t('notificationSettings.alerts.warning')}</p>
                                  <p className="text-sm text-amber-600">{t('notificationSettings.alerts.warningDesc')}</p>
                                </div>
                              </div>
                              <Toggle
                                enabled={config.rules.alertWarningEnabled}
                                onChange={(enabled) => updateRules({ alertWarningEnabled: enabled })}
                                label=""
                              />
                            </div>

                            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <div className="flex items-center gap-3">
                                <Info className="w-5 h-5 text-blue-600" />
                                <div>
                                  <p className="font-medium text-blue-800">{t('notificationSettings.alerts.info')}</p>
                                  <p className="text-sm text-blue-600">{t('notificationSettings.alerts.infoDesc')}</p>
                                </div>
                              </div>
                              <Toggle
                                enabled={config.rules.alertInfoEnabled}
                                onChange={(enabled) => updateRules({ alertInfoEnabled: enabled })}
                                label=""
                              />
                            </div>
                          </div>
                        </div>

                        {}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <h3 className="text-sm font-semibold text-slate-800 mb-4">{t('notificationSettings.alerts.channels')}</h3>
                          <div className="space-y-4">
                            {[
                              { key: 'breachChannels', label: 'Breach', color: 'red' },
                              { key: 'warningChannels', label: 'Warning', color: 'amber' },
                              { key: 'infoChannels', label: 'Info', color: 'blue' },
                            ].map((alertType) => (
                              <div key={alertType.key} className="flex items-center gap-4">
                                <span className={`w-24 text-sm font-medium text-${alertType.color}-700`}>
                                  {alertType.label}
                                </span>
                                <div className="flex gap-2">
                                  {['email', 'push', 'inApp'].map((channel) => (
                                    <button
                                      key={channel}
                                      onClick={() => {
                                        const current = config.rules[alertType.key];
                                        const updated = current.includes(channel)
                                          ? current.filter(c => c !== channel)
                                          : [...current, channel];
                                        updateRules({ [alertType.key]: updated });
                                      }}
                                      className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                                        config.rules[alertType.key].includes(channel)
                                          ? 'bg-blue-600 text-white border-blue-600'
                                          : 'bg-white text-slate-600 border-slate-300 hover:border-blue-500'
                                      }`}
                                    >
                                      {channel === 'email' ? 'Email' : channel === 'push' ? 'Push' : 'In-App'}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            {t('notificationSettings.alerts.timing')}
                          </h3>
                          <Toggle
                            enabled={config.rules.immediateAlerts}
                            onChange={(enabled) => updateRules({ immediateAlerts: enabled })}
                            label={t('notificationSettings.alerts.immediateAlerts')}
                            description={t('notificationSettings.alerts.immediateAlertsDesc')}
                          />
                          <div className="border-t border-slate-200 mt-3 pt-3">
                            <Toggle
                              enabled={config.rules.digestEnabled}
                              onChange={(enabled) => updateRules({ digestEnabled: enabled })}
                              label={t('notificationSettings.alerts.digest')}
                              description={t('notificationSettings.alerts.digestDesc')}
                            />
                            {config.rules.digestEnabled && (
                              <div className="mt-3 flex gap-4">
                                <select
                                  value={config.rules.digestFrequency}
                                  onChange={(e) => updateRules({ digestFrequency: e.target.value })}
                                  className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="hourly">{t('notificationSettings.alerts.hourly')}</option>
                                  <option value="daily">{t('notificationSettings.alerts.daily')}</option>
                                  <option value="weekly">{t('notificationSettings.alerts.weekly')}</option>
                                </select>
                                <input
                                  type="time"
                                  value={config.rules.digestTime}
                                  onChange={(e) => updateRules({ digestTime: e.target.value })}
                                  className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        {}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                            <VolumeX className="w-4 h-4" />
                            {t('notificationSettings.alerts.quietHours')}
                          </h3>
                          <Toggle
                            enabled={config.rules.quietHoursEnabled}
                            onChange={(enabled) => updateRules({ quietHoursEnabled: enabled })}
                            label={t('notificationSettings.alerts.enableQuietHours')}
                            description={t('notificationSettings.alerts.enableQuietHoursDesc')}
                          />
                          {config.rules.quietHoursEnabled && (
                            <div className="mt-3 space-y-3">
                              <div className="flex items-center gap-4">
                                <label className="text-sm text-slate-600">{t('notificationSettings.alerts.from')}</label>
                                <input
                                  type="time"
                                  value={config.rules.quietHoursStart}
                                  onChange={(e) => updateRules({ quietHoursStart: e.target.value })}
                                  className="px-3 py-2 border border-slate-300 rounded-lg"
                                />
                                <label className="text-sm text-slate-600">{t('notificationSettings.alerts.to')}</label>
                                <input
                                  type="time"
                                  value={config.rules.quietHoursEnd}
                                  onChange={(e) => updateRules({ quietHoursEnd: e.target.value })}
                                  className="px-3 py-2 border border-slate-300 rounded-lg"
                                />
                              </div>
                              <Toggle
                                enabled={config.rules.quietHoursExceptUrgent}
                                onChange={(enabled) => updateRules({ quietHoursExceptUrgent: enabled })}
                                label={t('notificationSettings.alerts.excludeUrgent')}
                                description={t('notificationSettings.alerts.excludeUrgentDesc')}
                              />
                            </div>
                          )}
                        </div>

                        {}
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <h3 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {t('notificationSettings.alerts.autoReports')}
                          </h3>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium text-slate-800">{t('notificationSettings.alerts.weeklyReport')}</p>
                                <p className="text-sm text-slate-500">{t('notificationSettings.alerts.weeklyReportDesc')}</p>
                              </div>
                              <Toggle
                                enabled={config.rules.weeklyReportEnabled}
                                onChange={(enabled) => updateRules({ weeklyReportEnabled: enabled })}
                                label=""
                              />
                            </div>
                            {config.rules.weeklyReportEnabled && (
                              <div className="flex items-center gap-4 pl-4">
                                <select
                                  value={config.rules.weeklyReportDay}
                                  onChange={(e) => updateRules({ weeklyReportDay: parseInt(e.target.value) })}
                                  className="px-3 py-2 border border-slate-300 rounded-lg"
                                >
                                  <option value={1}>{t('notificationSettings.alerts.monday')}</option>
                                  <option value={2}>{t('notificationSettings.alerts.tuesday')}</option>
                                  <option value={3}>{t('notificationSettings.alerts.wednesday')}</option>
                                  <option value={4}>{t('notificationSettings.alerts.thursday')}</option>
                                  <option value={5}>{t('notificationSettings.alerts.friday')}</option>
                                </select>
                                <input
                                  type="time"
                                  value={config.rules.weeklyReportTime}
                                  onChange={(e) => updateRules({ weeklyReportTime: e.target.value })}
                                  className="px-3 py-2 border border-slate-300 rounded-lg"
                                />
                              </div>
                            )}

                            <div className="flex items-center justify-between border-t border-slate-200 pt-4">
                              <div>
                                <p className="font-medium text-slate-800">{t('notificationSettings.alerts.monthlyReport')}</p>
                                <p className="text-sm text-slate-500">{t('notificationSettings.alerts.monthlyReportDesc')}</p>
                              </div>
                              <Toggle
                                enabled={config.rules.monthlyReportEnabled}
                                onChange={(enabled) => updateRules({ monthlyReportEnabled: enabled })}
                                label=""
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {}
              {activeTab === 'templates' && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 mb-6">{t('notificationSettings.templates.title')}</h2>

                  <div className="space-y-4">
                    {}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h3 className="text-sm font-semibold text-slate-800 mb-4">{t('notificationSettings.templates.customization')}</h3>
                      <Toggle
                        enabled={config.emailBranding.useCompanyBranding}
                        onChange={(enabled) => updateEmailBranding({ useCompanyBranding: enabled })}
                        label={t('notificationSettings.templates.useCompanyBranding')}
                        description={t('notificationSettings.templates.useCompanyBrandingDesc')}
                      />

                      {!config.emailBranding.useCompanyBranding && (
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            {t('notificationSettings.templates.headerColor')}
                          </label>
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={config.emailBranding.headerColor}
                              onChange={(e) => updateEmailBranding({ headerColor: e.target.value })}
                              className="w-10 h-10 rounded cursor-pointer"
                            />
                            <input
                              type="text"
                              value={config.emailBranding.headerColor}
                              onChange={(e) => updateEmailBranding({ headerColor: e.target.value })}
                              className="px-3 py-2 border border-slate-300 rounded-lg font-mono"
                            />
                          </div>
                        </div>
                      )}

                      <div className="mt-4">
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          {t('notificationSettings.templates.footerText')}
                        </label>
                        <textarea
                          value={config.emailBranding.footerText}
                          onChange={(e) => updateEmailBranding({ footerText: e.target.value })}
                          rows={2}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg resize-none"
                        />
                      </div>

                      <Toggle
                        enabled={config.emailBranding.includeUnsubscribeLink}
                        onChange={(enabled) => updateEmailBranding({ includeUnsubscribeLink: enabled })}
                        label={t('notificationSettings.templates.unsubscribeLink')}
                        description={t('notificationSettings.templates.unsubscribeLinkDesc')}
                      />
                    </div>

                    {}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h3 className="text-sm font-semibold text-slate-800 mb-4">{t('notificationSettings.templates.preview')}</h3>
                      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                        <div
                          className="h-3"
                          style={{ backgroundColor: config.emailBranding.useCompanyBranding ? theme.primaryColor : config.emailBranding.headerColor }}
                        />
                        <div className="p-4">
                          <div className="flex items-center gap-3 mb-4">
                            {config.emailBranding.useCompanyBranding && theme.logo ? (
                              <img src={theme.logo} alt="Logo" className="h-8 w-auto" />
                            ) : (
                              <div
                                className="w-8 h-8 rounded flex items-center justify-center text-white text-sm font-bold"
                                style={{ backgroundColor: config.emailBranding.useCompanyBranding ? theme.primaryColor : config.emailBranding.headerColor }}
                              >
                                CT
                              </div>
                            )}
                            <span className="font-semibold text-slate-800">
                              {config.emailBranding.useCompanyBranding ? theme.companyName : 'Covenant Tracker'}
                            </span>
                          </div>
                          <div className="text-slate-600 text-sm">
                            <p className="mb-2">{t('notificationSettings.templates.previewGreeting')}</p>
                            <p>{t('notificationSettings.templates.previewContent')}</p>
                          </div>
                        </div>
                        <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
                          {config.emailBranding.footerText}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {}
              {activeTab === 'webhooks' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-800">{t('notificationSettings.webhooks.title')}</h2>
                      <p className="text-sm text-slate-500">{t('notificationSettings.webhooks.subtitle')}</p>
                    </div>
                    <Toggle
                      enabled={config.webhooks.enabled}
                      onChange={(enabled) => updateRules({ webhooksEnabled: enabled })}
                      label=""
                    />
                  </div>

                  {config.webhooks.enabled && (
                    <div className="space-y-4">
                      {}
                      {config.webhooks.endpoints.length > 0 ? (
                        <div className="space-y-3">
                          {config.webhooks.endpoints.map((webhook) => (
                            <div
                              key={webhook.id}
                              className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-medium text-slate-800">{webhook.name}</h4>
                                  <p className="text-sm text-slate-500 font-mono truncate max-w-md">
                                    {webhook.url}
                                  </p>
                                  <div className="flex gap-2 mt-2">
                                    {webhook.events.map((event) => (
                                      <span
                                        key={event}
                                        className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full"
                                      >
                                        {event}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                                <button
                                  onClick={() => removeWebhook(webhook.id)}
                                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-slate-500">
                          <Webhook className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                          <p>{t('notificationSettings.webhooks.noWebhooks')}</p>
                        </div>
                      )}

                      {}
                      {showWebhookForm ? (
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <h3 className="font-semibold text-blue-800 mb-4">{t('notificationSettings.webhooks.newWebhook')}</h3>
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">
                                {t('notificationSettings.webhooks.name')} *
                              </label>
                              <input
                                type="text"
                                value={newWebhook.name}
                                onChange={(e) => setNewWebhook(prev => ({ ...prev, name: e.target.value }))}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                                placeholder="Mon webhook"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">
                                {t('notificationSettings.webhooks.url')} *
                              </label>
                              <input
                                type="url"
                                value={newWebhook.url}
                                onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono"
                                placeholder="https://example.com/webhook"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">
                                {t('notificationSettings.webhooks.events')}
                              </label>
                              <div className="flex flex-wrap gap-2">
                                {['breach', 'warning', 'info', 'report'].map((event) => (
                                  <button
                                    key={event}
                                    onClick={() => {
                                      const events = newWebhook.events.includes(event)
                                        ? newWebhook.events.filter(e => e !== event)
                                        : [...newWebhook.events, event];
                                      setNewWebhook(prev => ({ ...prev, events }));
                                    }}
                                    className={`px-3 py-1.5 text-sm rounded-lg border ${
                                      newWebhook.events.includes(event)
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white text-slate-600 border-slate-300'
                                    }`}
                                  >
                                    {event}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">
                                {t('notificationSettings.webhooks.secret')}
                              </label>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={newWebhook.secret}
                                  onChange={(e) => setNewWebhook(prev => ({ ...prev, secret: e.target.value }))}
                                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg font-mono"
                                  placeholder={t('notificationSettings.webhooks.secretPlaceholder')}
                                />
                                <button
                                  onClick={generateSecret}
                                  className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg"
                                >
                                  {t('notificationSettings.webhooks.generate')}
                                </button>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={handleAddWebhook}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
                              >
                                {t('notificationSettings.webhooks.add')}
                              </button>
                              <button
                                onClick={() => setShowWebhookForm(false)}
                                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg"
                              >
                                {t('common.cancel')}
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowWebhookForm(true)}
                          className="w-full p-4 border-2 border-dashed border-slate-300 hover:border-blue-500 rounded-lg text-slate-500 hover:text-blue-600 flex items-center justify-center gap-2 transition-colors"
                        >
                          <Plus className="w-5 h-5" />
                          {t('notificationSettings.webhooks.addWebhook')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {}
              {activeTab === 'advanced' && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-800 mb-6">{t('notificationSettings.advanced.title')}</h2>

                  <div className="space-y-6">
                    {}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h3 className="text-sm font-semibold text-slate-800 mb-4">{t('notificationSettings.advanced.limits')}</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            {t('notificationSettings.advanced.maxEmailsPerDay')}
                          </label>
                          <input
                            type="number"
                            value={config.rules.maxEmailsPerDay}
                            onChange={(e) => updateRules({ maxEmailsPerDay: parseInt(e.target.value) })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                            min={1}
                            max={1000}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            {t('notificationSettings.advanced.maxPushPerHour')}
                          </label>
                          <input
                            type="number"
                            value={config.rules.maxPushPerHour}
                            onChange={(e) => updateRules({ maxPushPerHour: parseInt(e.target.value) })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                            min={1}
                            max={100}
                          />
                        </div>
                      </div>
                    </div>

                    {}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h3 className="text-sm font-semibold text-slate-800 mb-4">{t('notificationSettings.advanced.logging')}</h3>
                      <Toggle
                        enabled={config.logging.enabled}
                        onChange={(enabled) => updateRules({ loggingEnabled: enabled })}
                        label={t('notificationSettings.advanced.enableLogs')}
                        description={t('notificationSettings.advanced.enableLogsDesc')}
                      />
                      {config.logging.enabled && (
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-slate-700 mb-1">
                            {t('notificationSettings.advanced.logRetention')}
                          </label>
                          <input
                            type="number"
                            value={config.logging.retentionDays}
                            onChange={(e) => updateRules({ loggingRetentionDays: parseInt(e.target.value) })}
                            className="w-32 px-3 py-2 border border-slate-300 rounded-lg"
                            min={1}
                            max={365}
                          />
                        </div>
                      )}
                    </div>

                    {}
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <h3 className="text-sm font-semibold text-red-800 mb-2">{t('notificationSettings.advanced.dangerZone')}</h3>
                      <p className="text-sm text-red-600 mb-4">
                        {t('notificationSettings.advanced.dangerZoneDesc')}
                      </p>
                      <button
                        onClick={() => {
                          if (confirm(t('notificationSettings.advanced.confirmReset'))) {
                            resetConfig();
                            setMessage({ type: 'success', text: t('notificationSettings.messages.configReset') });
                          }
                        }}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg flex items-center gap-2"
                      >
                        <RefreshCw className="w-4 h-4" />
                        {t('notificationSettings.advanced.resetConfig')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {}
              <div className="mt-8 pt-6 border-t border-slate-200 flex justify-end">
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('notificationSettings.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {t('notificationSettings.save')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;